import * as THREE from 'three';
import gsap from 'gsap';
import { ScrollSmoother } from 'gsap/ScrollSmoother.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import DragRotate from '../utils/DragRotate.js';

/*
 * Modèle 3D de la page single-accessoire.
 *
 * - Le modèle tourne sur lui-même en permanence (data-spin-speed, en radians/seconde).
 * - Pendant le scroll, la vitesse augmente selon la vitesse de défilement
 *   (data-spin-boost), puis revient doucement à la vitesse de base.
 *
 * Utilisation :
 *   <div class="accessoire-model"
 *        data-component="AccessoireModel"
 *        data-model="assets/models/ac/scene.gltf"
 *        data-spin-speed="0.4"
 *        data-spin-boost="0.004"
 *        data-stop-at=".compatibilite">
 *       <canvas class="webgl"></canvas>
 *   </div>
 *
 * - On peut faire tourner et incliner le modèle à la souris (clic maintenu + glisser).
 *
 * - Quand la section data-stop-at (par défaut .compatibilite) arrive en bas de
 *   l'écran, le modèle arrête de rester fixe et défile avec la page.
 *
 * Place cette div À L'EXTÉRIEUR de #smooth-wrapper pour qu'elle reste
 * fixe à gauche de l'écran pendant le scroll.
 * N'utilise pas data-speed / data-lag : ScrollSmoother (effects: true)
 * les lit comme des réglages de parallax.
 */

const FOV = 30; // champ de vision de la caméra (degrés)
const FIT = 0.85; // part de la hauteur du canvas occupée par le modèle

export default class AccessoireModel {
  constructor(element) {
    this.element = element;
    this.canvas = element.querySelector('.webgl');

    if (!this.canvas) {
      console.warn('AccessoireModel : il manque un <canvas class="webgl">.');
      return;
    }

    this.modelUrl = element.dataset.model || 'assets/models/ac/scene.gltf';

    this.reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    // Vitesse de rotation de base (rad/s) et force de l'accélération au scroll
    this.baseSpeed = parseFloat(element.dataset.spinSpeed) || 0.4;
    this.boost = parseFloat(element.dataset.spinBoost) || 0.004;
    this.maxBoost = parseFloat(element.dataset.spinMaxBoost) || 12; // rad/s ajoutés au maximum

    // Section à partir de laquelle le modèle arrête de suivre l'écran
    this.stopTarget = document.querySelector(
      element.dataset.stopAt || '.compatibilite',
    );
    this.offsetY = 0;

    this.extraSpeed = 0; // vitesse ajoutée par le scroll (lissée)
    this.lastScroll = this.getScroll();
    this.width = 0;
    this.height = 0;
    this.model = null;

    this.tick = this.tick.bind(this);
    this.init();
  }

  init() {
    this.createRenderer();
    this.createScene();
    this.loadModel();

    // Rotation du modèle à la souris (le canvas ne capte pas la souris)
    this.dragRotate = new DragRotate({
      hitTest: (x, y) => this.isOverModel(x, y),
    });

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.element);
    this.resize();

    // Même ticker que ScrollSmoother : rendu synchro avec le défilement
    gsap.ticker.add(this.tick);
  }

  /* ---------- Three.js ---------- */

  createRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      alpha: true,
      antialias: true,
    });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
  }

  createScene() {
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(FOV, 1, 0.1, 100);

    const ambient = new THREE.AmbientLight('#ffffff', 1.6);
    const key = new THREE.DirectionalLight('#ffffff', 2.6);
    key.position.set(-1, 2, 3);
    const fill = new THREE.DirectionalLight('#ffffff', 0.8);
    fill.position.set(2, 0.5, 1);
    this.scene.add(ambient, key, fill);

    // tilt : inclinaison à la souris
    this.tilt = new THREE.Group();
    this.scene.add(this.tilt);

    // spinner : tourne autour de l'axe vertical (Y) du monde
    this.spinner = new THREE.Group();
    this.tilt.add(this.spinner);
  }

  loadModel() {
    const loader = new GLTFLoader();
    loader.load(
      this.modelUrl,
      (gltf) => this.setModel(gltf.scene),
      undefined,
      (error) =>
        console.warn(
          `AccessoireModel : impossible de charger ${this.modelUrl}`,
          error,
        ),
    );
  }

  setModel(source) {
    source.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(source);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;

    // Recentre le modèle sur son milieu
    source.position.copy(center).multiplyScalar(-1);

    // Normalise la taille à 1 unité
    const normalized = new THREE.Group();
    normalized.scale.setScalar(1 / maxDim);
    normalized.add(source);

    // Ton modèle est long sur l'axe Z (z de 0 à 120) : on le redresse pour que
    // son grand axe soit vertical, puis on le fait tourner autour de ce même axe.
    // Si le modèle est à l'envers, remplace par Math.PI / 2.
    const orient = new THREE.Group();
    orient.rotation.x = -Math.PI / 2;
    orient.rotation.z = Math.PI; // Flip upside down
    orient.add(normalized);

    this.spinner.add(orient);
    this.model = orient;
    this.resize();
  }

  resize() {
    const width = this.element.clientWidth;
    const height = this.element.clientHeight;
    if (!width || !height) return;

    this.width = width;
    this.height = height;

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(width, height, false);

    this.camera.aspect = width / height;

    // Le modèle fait 1 unité de haut : on place la caméra pour qu'il remplisse FIT
    const fitHeight = 1 / FIT;
    const fitWidth = 1 / FIT / this.camera.aspect;
    const distance =
      Math.max(fitHeight, fitWidth) /
      2 /
      Math.tan(THREE.MathUtils.degToRad(FOV / 2));
    this.camera.position.set(0, 0, distance);
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();
  }

  // Le pointeur est-il au-dessus du modèle ?
  isOverModel(x, y) {
    if (!this.model) return false;

    const rect = this.element.getBoundingClientRect();

    // Sur mobile/tablette, le calque couvre tout l'écran derrière le texte : pas de glissement
    if (rect.width > window.innerWidth * 0.9) return false;

    const centerY = rect.top + rect.height / 2;
    const halfHeight = (rect.height * FIT) / 2;

    return (
      x >= rect.left && x <= rect.right && Math.abs(y - centerY) <= halfHeight
    );
  }

  /* ---------- Scroll ---------- */

  // Position réelle du scroll (compatible ScrollSmoother)
  getScroll() {
    const smoother = ScrollSmoother.get && ScrollSmoother.get();
    return smoother ? smoother.scrollTop() : window.scrollY;
  }

  // Le modèle reste fixe, puis monte avec la page quand la section d'arrêt
  // atteint le bas de sa zone. On lit la position réelle de la section à chaque
  // image : avec ScrollSmoother, c'est la position lissée, donc parfaitement synchro.
  updatePosition() {
    if (!this.stopTarget) return;

    const top = this.stopTarget.getBoundingClientRect().top;
    const offset = Math.min(0, top - this.element.offsetHeight);

    if (offset !== this.offsetY) {
      this.offsetY = offset;
      this.element.style.transform = `translate3d(0, ${offset}px, 0)`;
    }
  }

  tick(time, deltaTime) {
    this.updatePosition();

    if (!this.model || !this.width) return;

    const dt = Math.min(deltaTime, 100) / 1000;

    // Vitesse du scroll en px/s (valeur absolue : le modèle tourne toujours dans le même sens)
    const scroll = this.getScroll();
    const velocity = dt > 0 ? Math.abs(scroll - this.lastScroll) / dt : 0;
    this.lastScroll = scroll;

    // Vitesse ajoutée visée, puis lissage (monte vite, redescend doucement)
    const target = this.reduceMotion
      ? 0
      : Math.min(velocity * this.boost, this.maxBoost);
    const ease = target > this.extraSpeed ? 0.15 : 0.04;
    this.extraSpeed += (target - this.extraSpeed) * ease;

    // Rotation automatique (en pause pendant le glissement) + rotation à la souris
    const speed = this.reduceMotion ? 0 : this.baseSpeed;
    const auto = this.dragRotate.dragging ? 0 : (speed + this.extraSpeed) * dt;
    this.spinner.rotation.y += auto + this.dragRotate.update(dt);
    this.tilt.rotation.x = this.dragRotate.tilt;

    this.renderer.render(this.scene, this.camera);
  }

  destroy() {
    gsap.ticker.remove(this.tick);
    this.dragRotate?.destroy();
    this.resizeObserver?.disconnect();
    this.renderer?.dispose();
  }
}
